import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { updateGroupBuyPool } from '../../services/groupBuyService';

const baseStatusOptions = [
  { value: 'closed', label: 'Closed' },
  { value: 'purchased', label: 'Purchased' },
  { value: 'in_warehouse', label: 'In Warehouse' },
  { value: 'refund', label: 'Refund' },
  { value: 'cancelled', label: 'Cancelled' },
];

const EditPoolModal = ({ open, pool, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dynamically create status options including current pool status if not already present
  const statusOptions = useMemo(() => {
    if (!pool?.status) return baseStatusOptions;

    const currentStatusExists = baseStatusOptions.some(option => option.value === pool.status);
    if (currentStatusExists) return baseStatusOptions;

    // Add current status to the beginning if it's not in the base options
    const currentStatusOption = { value: pool.status, label: pool.status.charAt(0).toUpperCase() + pool.status.slice(1).replace('_', ' ') };
    return [currentStatusOption, ...baseStatusOptions];
  }, [pool?.status]);

  // Platform options
  const platformOptions = [
    { value: '', label: 'Not specified' },
    { value: '1688', label: '1688' },
    { value: 'pinduoduo', label: 'Pinduoduo' },
    { value: 'taobao', label: 'Taobao' },
    { value: 'alibaba', label: 'Alibaba' },
  ];

  // Account options
  const accountOptions = [
    { value: '', label: 'Not specified' },
    { value: 'Samuel202020', label: 'Samuel202020' },
    { value: '13864927809(pdd)', label: '13864927809(pdd)' },
  ];

  // Check if status can be updated
  const canUpdateStatus = () => {
    if (!pool) return false;
    // Cannot update if already in final states (fulfilled, refund, cancelled)
    const finalStatuses = ['fulfilled', 'refund', 'cancelled'];
    return !finalStatuses.includes(pool.status);
  };
  const [formData, setFormData] = useState({
    // Pool basic info
    status: '',

    // Purchase info (from PoolPurchase model)
    actual_purchased_price: '',
    goods_type: 'normal',
    purchasing_platform: '',
    purchasing_account: '',
    image: null, // For warehouse photo upload
  });

  useEffect(() => {
    if (pool && open) {
      setFormData({
        status: pool.status || '',
        actual_purchased_price: pool.purchase_info?.actual_purchased_price?.toString() || '',
        goods_type: pool.purchase_info?.goods_type || 'normal',
        purchasing_platform: pool.purchase_info?.purchasing_platform || '',
        purchasing_account: pool.purchase_info?.purchasing_account || '',
        image: null, // Reset image on modal open
      });
      setError(null);
    }
  }, [pool, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!pool) return;

    setLoading(true);
    setError(null);

    try {
      const updateData = {
        // Status update (only if allowed)
        ...(canUpdateStatus() && formData.status && { status: formData.status }),

        // Purchase information
        actual_purchased_price: formData.actual_purchased_price ? parseFloat(formData.actual_purchased_price) : null,
        goods_type: formData.goods_type,
        purchasing_platform: formData.purchasing_platform || null,
        purchasing_account: formData.purchasing_account || null,

        // Image upload (only for in_warehouse status)
        ...(formData.status === 'in_warehouse' && formData.image && { image: formData.image }),
      };

      // Update pool and purchase info
      await updateGroupBuyPool(pool.id, updateData);

      if (onSave) {
        onSave();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update pool');
    } finally {
      setLoading(false);
    }
  };

  if (!pool) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Edit Pool Details: {pool.title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Pool Information */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mb: 2 }}>
              Pool Information
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small" disabled={!canUpdateStatus()}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    disabled={!canUpdateStatus()}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!canUpdateStatus() && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Status cannot be updated after the pool reaches a final state
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2">Progress:</Typography>
                  <Typography variant="body2">
                    {pool.current_quantity}/{pool.target_quantity} participants
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Purchase Information */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mb: 2 }}>
              Purchase Information
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Actual Purchased Price"
                  type="number"
                  value={formData.actual_purchased_price}
                  onChange={(e) => handleInputChange('actual_purchased_price', e.target.value)}
                  size="small"
                  inputProps={{ step: 0.01, min: 0 }}
                  helperText="Price paid to supplier"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Goods Type</InputLabel>
                  <Select
                    value={formData.goods_type}
                    label="Goods Type"
                    onChange={(e) => handleInputChange('goods_type', e.target.value)}
                  >
                    <MenuItem value="normal">Normal Goods</MenuItem>
                    <MenuItem value="special">Special/Sensitive Goods</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Purchasing Platform</InputLabel>
                  <Select
                    value={formData.purchasing_platform}
                    label="Purchasing Platform"
                    onChange={(e) => handleInputChange('purchasing_platform', e.target.value)}
                  >
                    {platformOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Purchasing Account</InputLabel>
                  <Select
                    value={formData.purchasing_account}
                    label="Purchasing Account"
                    onChange={(e) => handleInputChange('purchasing_account', e.target.value)}
                  >
                    {accountOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

          </Paper>

          {/* Warehouse Photo Upload - Only show when status is in_warehouse */}
          {formData.status === 'in_warehouse' && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mb: 2 }}>
                Warehouse Photo Upload
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ mb: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="warehouse-photo-upload"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setFormData(prev => ({
                            ...prev,
                            image: file
                          }));
                        }
                      }}
                    />
                    <label htmlFor="warehouse-photo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                        sx={{ height: 100, border: '2px dashed #ccc', borderRadius: 2 }}
                      >
                        {formData.image ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="success.main">
                              ✓ {formData.image.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Click to change image
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: 'center' }}>
                            <CloudUploadIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
                            <Typography variant="body2">
                              Upload Warehouse Photo
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              JPG, PNG up to 10MB
                            </Typography>
                          </Box>
                        )}
                      </Button>
                    </label>
                  </Box>

                  {/* Preview selected image */}
                  {formData.image && (
                    <Card sx={{ maxWidth: 200, mt: 1 }}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={URL.createObjectURL(formData.image)}
                        alt="Warehouse photo preview"
                        sx={{ objectFit: 'cover' }}
                      />
                    </Card>
                  )}

                  {/* Show existing warehouse photo if available */}
                  {pool?.purchase_info?.image && !formData.image && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Current warehouse photo:
                      </Typography>
                      <Card sx={{ maxWidth: 200 }}>
                        <CardMedia
                          component="img"
                          height="140"
                          image={pool.purchase_info.image}
                          alt="Current warehouse photo"
                          sx={{ objectFit: 'cover' }}
                        />
                      </Card>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPoolModal;
